/* ============================================================
 * Build-Your-Own Pack — product page logic
 *
 * Customer mixes any colors in multiples of 7. Per-spool price drops
 * at 7 / 14 / 21+ tiers (set via the section's price config). Any
 * non-multiple of 7 is invalid — CTA stays disabled.
 *
 * Cart line item properties:
 *   _purchase_mode: 'mix_match'
 *   _bundle_id:     groups same-customer adds so the cart guard can
 *                   treat them as one logical bundle
 * ============================================================ */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-byop]';

  function fmtMoney(amount) {
    // Liquid `| json` emits prices as JSON strings (e.g. "10.99"), so
    // coerce defensively — toFixed on a string would throw and abort
    // updateByo() silently.
    const n = typeof amount === 'number' ? amount : parseFloat(amount);
    return '$' + (isNaN(n) ? 0 : n).toFixed(2);
  }

  function tileBg(v) {
    if (v && v.image) return "url(" + JSON.stringify(v.image) + ")";
    return spoolBgDataURI(v && v.hex ? v.hex : '#bdbdbd');
  }
  function spoolBgDataURI(hex) {
    const c = hex;
    const svg =
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>" +
      "<circle cx='50' cy='50' r='48' fill='" + c + "'/>" +
      "<circle cx='50' cy='50' r='15' fill='%23f5f3ee' stroke='rgba(0,0,0,0.18)' stroke-width='0.6'/>" +
      "<circle cx='50' cy='50' r='4' fill='%231a1a1a'/>" +
      "</svg>";
    return "url(\"data:image/svg+xml," + svg.replace(/#/g, '%23').replace(/\s+/g, ' ') + "\")";
  }

  // SINGLE TIER MODE: flat per-spool rate (P.sale) for any multiple of 7.
  // No 14/21+ inflections — the rate is the same whether the customer
  // buys 7, 14, or 21+ spools. Anything that isn't a multiple of 7 is
  // invalid (CTA disabled, nudge to next multiple).
  function priceFor(n, P) {
    return P.sale;
  }
  function activeTier(n) {
    // Single tier — light up the card the moment the customer reaches
    // a valid multiple of 7.
    return (n >= 7 && n % 7 === 0) ? 7 : null;
  }
  function progressPct(n) {
    // 7 = 100% (the only goal). Below 7 fills proportionally; above 7
    // stays full so customers building larger packs don't lose feedback.
    if (n >= 7) return 100;
    return (n / 7) * 100;
  }
  function isValid(n, completing) {
    if (completing) return n === completing.need;
    return n >= 7 && n % 7 === 0;
  }

  // Treats ALL mix_match lines in the cart as a single logical bundle.
  // Customer doesn't care about internal bundle_ids — they care about
  // "do I have a multiple of 7 spools across my whole BYO cart". Returns
  // an object describing the aggregate deficit, or null if the cart is
  // valid (or has no BYO items at all).
  //
  // `id` is the first existing bundle_id we see — new completion adds
  // reuse it so the cart converges to one bundle over time.
  function findIncompleteBundleInCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        if (!cart || !cart.items) return null;
        let total = 0;
        let firstBundleId = null;
        for (const item of cart.items) {
          const props = item.properties || {};
          if (props._purchase_mode !== 'mix_match') continue;
          total += item.quantity;
          if (!firstBundleId && props._bundle_id) firstBundleId = props._bundle_id;
        }
        if (total === 0 || total % 7 === 0) return null;
        const need = 7 - (total % 7);
        return { id: firstBundleId, total: total, need: need, target: total + need };
      })
      .catch(() => null);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initOne);
  }

  function initOne(root) {
    const dataEl = root.querySelector('[data-byop-data]');
    if (!dataEl) return;

    const allRoots = document.querySelectorAll('[data-byop]');
    if (allRoots.length > 1) {
      console.warn('[byop] Multiple snippet instances on the page:', allRoots.length, '- state will not sync between them.');
    }

    let model;
    try { model = JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[byop] data parse error', e); return; }

    const P = model.prices;
    const variants = model.variants;
    const hexMap   = model.hexMap || {};

    // Resolve a final hex per variant: prefer the metafield value, else the
    // baked-in color-name map, else neutral gray.
    function normalizeName(s) {
      return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    function resolveHex(v) {
      if (v.hex && v.hex.toLowerCase() !== '#bdbdbd') return v.hex;
      const mapped = hexMap[normalizeName(v.color || v.title)];
      return mapped || v.hex || '#bdbdbd';
    }
    variants.forEach(v => { v.hex = resolveHex(v); });

    const byoQty = variants.map(() => 0);
    // BYO completion mode — set when an in-cart bundle needs more spools.
    // While set, the validator accepts ONLY the exact `need` count and
    // the cart-add reuses the existing bundle's _bundle_id so the lines
    // merge with the partial bundle already in the cart.
    let completingBundle = null;

    const byoGrid    = root.querySelector('[data-byo-grid]');
    const byoCountEls   = root.querySelectorAll('[data-byo-count]');
    const byoTotalEls   = root.querySelectorAll('[data-byo-total]');
    const byoCta     = root.querySelector('[data-byo-cta]');
    const byoCtaLblEls  = root.querySelectorAll('[data-byo-cta-label]');
    const byoProgs     = root.querySelectorAll('[data-byo-progress-top],[data-byo-progress-bot]');
    const byoStatuses  = root.querySelectorAll('[data-byo-status-top],[data-byo-status-bot]');
    const byoMetaEls    = root.querySelectorAll('[data-byo-meta]');
    const tierCards  = root.querySelectorAll('[data-tier]');
    const completingBanner = root.querySelector('[data-byop-completing]');
    const completingText   = root.querySelector('[data-byop-completing-text]');

    function setText(els, text)  { els.forEach(el => { if (el) el.textContent = text; }); }
    function setStatus(text, isWarn) {
      byoStatuses.forEach(el => {
        if (!el) return;
        el.textContent = text;
        el.classList.toggle('is-warn', !!isWarn);
      });
    }
    function setProgress(pct) {
      byoProgs.forEach(el => { if (el) el.style.width = pct + '%'; });
    }

    function renderByoGrid() {
      byoGrid.innerHTML = variants.map((v, i) => {
        const q = byoQty[i];
        const name = escapeHtml(v.color || v.title);

        // Out-of-stock variants render a dimmed tile with a "Sold out"
        // pill and no qty controls, so they can't be added.
        if (v.available === false) {
          return (
            '<div class="byop-tile is-oos" data-i="' + i + '" aria-label="' + name + ' (sold out)" aria-disabled="true">' +
              '<div class="byop-tile__sw" style=\'background-image:' + tileBg(v) + '\'></div>' +
              '<div class="byop-tile__name">' + name + '</div>' +
              '<div class="byop-tile__oos">Sold out</div>' +
            '</div>'
          );
        }

        const decDisabled = q === 0 ? 'disabled' : '';
        return (
          '<div class="byop-tile' + (q > 0 ? ' has' : '') + '" data-i="' + i + '">' +
            '<div class="byop-tile__sw" style=\'background-image:' + tileBg(v) + '\'></div>' +
            '<div class="byop-tile__name">' + name + '</div>' +
            '<div class="byop-qrow">' +
              '<button type="button" class="byop-qbtn" data-act="d" data-i="' + i + '" ' + decDisabled + ' aria-label="Decrease ' + name + '">−</button>' +
              '<span class="byop-qval">' + q + '</span>' +
              '<button type="button" class="byop-qbtn" data-act="i" data-i="' + i + '" aria-label="Increase ' + name + '">+</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');
    }

    function updateByo() {
      const n = byoQty.reduce((a, b) => a + b, 0);
      const t = activeTier(n);
      const pps = priceFor(n, P);

      // ============ COMPLETION-MODE BRANCH ============
      // If there's an incomplete bundle waiting in the cart, the customer
      // is here to top it off — relax the multiples-of-7 rule and let
      // them add EXACTLY the deficit.
      if (completingBundle) {
        const need = completingBundle.need;
        const finalTotal = completingBundle.target;
        tierCards.forEach(card => card.classList.remove('is-active'));
        setProgress(Math.min(100, (n / need) * 100));
        setText(byoCountEls, n + ' of ' + need + ' picked');
        const picked = byoQty.filter(q => q > 0).length;
        setText(byoMetaEls, picked === 0
          ? 'Pick ' + need + ' to complete your bundle'
          : picked + ' color' + (picked === 1 ? '' : 's') + ' selected');

        // Sale rate while completing — matches what the customer will
        // actually be charged once the bundle is whole.
        const runningTotal = parseFloat(P.sale) * n;
        setText(byoTotalEls, fmtMoney(runningTotal));

        if (n === 0) {
          setStatus('Pick ' + need + ' more spool' + (need === 1 ? '' : 's') + ' to complete your bundle (will be ' + finalTotal + ' total)', false);
          byoCta.disabled = true;
          setText(byoCtaLblEls, 'Add to cart');
        } else if (n === need) {
          setStatus('Ready to complete your bundle — total will be ' + finalTotal + ' spools', false);
          byoCta.disabled = false;
          setText(byoCtaLblEls, 'Complete bundle — add ' + n + ' spool' + (n === 1 ? '' : 's'));
        } else if (n < need) {
          setStatus('Pick ' + (need - n) + ' more to reach ' + need + ' (completes bundle to ' + finalTotal + ')', true);
          byoCta.disabled = true;
          setText(byoCtaLblEls, 'Add to cart');
        } else {
          setStatus('Too many — your bundle only needs ' + need + ' more (currently picked ' + n + ')', true);
          byoCta.disabled = true;
          setText(byoCtaLblEls, 'Add to cart');
        }
        return;
      }

      // ============ NORMAL BRANCH (no in-cart bundle to complete) ============
      setProgress(Math.min(100, progressPct(n)));

      tierCards.forEach(card => card.classList.remove('is-active'));
      if (t) {
        const active = root.querySelector('[data-tier="' + t + '"]');
        if (active) active.classList.add('is-active');
      }

      setText(byoCountEls, n + ' spool' + (n === 1 ? '' : 's'));

      const picked = byoQty.filter(q => q > 0).length;
      setText(byoMetaEls, picked === 0
        ? 'Mix any colors'
        : (picked + ' color' + (picked === 1 ? '' : 's') + ' selected'));

      if (n === 0) {
        setStatus('Add spools to build your pack', false);
        setText(byoTotalEls, fmtMoney(0));
        byoCta.disabled = true;
        setText(byoCtaLblEls, 'Add to cart');
        return;
      }
      const totalAmt = pps * n;
      const totalStr = fmtMoney(totalAmt);

      if (n >= 7 && n % 7 === 0) {
        // Valid pack — flat pack-deal rate.
        const packs = n / 7;
        const packLabel = packs === 1
          ? '7-Pack rate'
          : packs + ' × 7-Pack rate';
        setStatus(packLabel + ' · ' + fmtMoney(pps) + '/spool', false);
        setText(byoTotalEls, totalStr);
        byoCta.disabled = false;
        setText(byoCtaLblEls, 'Add to cart — ' + totalStr);
      } else {
        // Not on a multiple of 7 — nudge to the next one. Running total
        // uses the flat pack-deal rate (it's the only rate).
        const need = 7 - (n % 7);
        const nextTierQty = n + need;
        const runningTotal = parseFloat(P.sale) * n;
        setStatus('Add ' + need + ' more to reach ' + nextTierQty + ' (multiples of 7 only)', true);
        setText(byoTotalEls, fmtMoney(runningTotal));
        byoCta.disabled = true;
        setText(byoCtaLblEls, 'Add to cart');
      }
    }

    function setCompletingBundle(bundle) {
      completingBundle = bundle;
      if (!completingBanner) return;
      if (bundle) {
        if (completingText) {
          completingText.textContent =
            'You have ' + bundle.total + ' spools in your cart from a previous selection. ' +
            'Pick ' + bundle.need + ' more below to complete the bundle at the pack rate.';
        }
        completingBanner.removeAttribute('hidden');
      } else {
        completingBanner.setAttribute('hidden', '');
      }
      updateByo();
    }

    byoGrid.addEventListener('click', e => {
      const b = e.target.closest('.byop-qbtn'); if (!b) return;
      const i = +b.dataset.i;
      // Safety guard — the qbtn buttons aren't rendered on OOS tiles,
      // but a stray DOM tamper shouldn't be able to add sold-out stock.
      if (variants[i] && variants[i].available === false) return;
      if (b.dataset.act === 'i') byoQty[i]++;
      else if (byoQty[i] > 0) byoQty[i]--;
      renderByoGrid();
      updateByo();
    });

    byoCta.addEventListener('click', () => addByoToCart());

    function addByoToCart() {
      const n = byoQty.reduce((a, b) => a + b, 0);
      if (!isValid(n, completingBundle)) return;

      // Always look up any existing mix_match bundle in the cart and reuse
      // its id, so a single customer cart has at most ONE BYO bundle. This
      // keeps the cart-guard's aggregate validation aligned with reality —
      // no fragmented bundle_ids piling up across multiple adds.
      //
      // Properties are kept minimal + identical across adds so Shopify
      // merges same-variant lines into one.
      fetchExistingBundleId().then(existingId => {
        const bundleId = completingBundle ? completingBundle.id
                         : existingId      ? existingId
                         : ('byo-' + Date.now());

        const items = [];
        byoQty.forEach((q, i) => {
          if (q <= 0) return;
          const v = variants[i];
          items.push({
            id: v.id,
            quantity: q,
            properties: {
              _purchase_mode: 'mix_match',
              _bundle_id: bundleId,
            },
          });
        });
        addItemsToCart(byoCta, items, function () {
          findIncompleteBundleInCart().then(b => setCompletingBundle(b));
          for (let k = 0; k < byoQty.length; k++) byoQty[k] = 0;
          renderByoGrid();
        });
      });
    }

    function fetchExistingBundleId() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => {
          if (!cart || !cart.items) return null;
          for (const item of cart.items) {
            const props = item.properties || {};
            if (props._purchase_mode === 'mix_match' && props._bundle_id) {
              return props._bundle_id;
            }
          }
          return null;
        })
        .catch(() => null);
    }

    renderByoGrid();
    updateByo();

    findIncompleteBundleInCart().then(setCompletingBundle);

    // Re-check whenever the cart changes from elsewhere (cart drawer
    // remove, qty edit, etc.). Without this, a customer who removes a
    // bundle line in the drawer while still on the product page would not
    // see the completion banner appear until they manually refresh.
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, function () {
        findIncompleteBundleInCart().then(setCompletingBundle);
      });
    }
    document.addEventListener('cart:refresh', function () {
      findIncompleteBundleInCart().then(setCompletingBundle);
    });

    function addItemsToCart(btn, items, onSuccess) {
      if (!items.length) return;
      btn.classList.add('is-loading');

      const drawer = document.querySelector('m-cart-drawer');
      // Ask Shopify to return rendered Section HTML inline with the add
      // response — only reliable way to update the drawer without a full
      // page refresh. The "cart-drawer" section ID matches sections/cart-drawer.liquid.
      const body = {
        items: items,
        sections: drawer ? 'cart-drawer' : undefined,
        sections_url: window.location.pathname,
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json().then(data => ({ ok: r.ok, status: r.status, data })))
        .then(({ ok, status, data }) => {
          btn.classList.remove('is-loading');
          if (!ok) {
            const msg = (data && (data.description || data.message)) || ('Could not add to cart (' + status + ')');
            console.warn('[byop] cart add failed:', msg, data);
            flashError(btn, msg);
            return;
          }

          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data);
          } else if (drawer && typeof drawer.open === 'function') {
            drawer.open();
            if (typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
          } else {
            window.location.href = '/cart';
            return;
          }

          fetch('/cart.js').then(r => r.json()).then(cart => {
            document.querySelectorAll('.m-cart-count-bubble').forEach(el => {
              if (cart.item_count > 0) {
                el.textContent = cart.item_count;
                el.classList.remove('m:hidden');
                el.classList.remove('hidden');
              }
            });
            if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
              window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, cart);
            }
            if (typeof onSuccess === 'function') onSuccess(cart);
          }).catch(() => {
            if (typeof onSuccess === 'function') onSuccess(null);
          });
        }).catch(err => {
          btn.classList.remove('is-loading');
          console.error('[byop] cart add error', err);
          flashError(btn, 'Network error. Please try again.');
        });
    }

    function flashError(btn, msg) {
      const lbl = btn.querySelector('[data-byo-cta-label]');
      if (!lbl) return;
      const original = lbl.textContent;
      lbl.textContent = msg.length > 60 ? msg.slice(0, 60) + '…' : msg;
      setTimeout(() => { lbl.textContent = original; }, 3000);
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }
})();
